import { Fragment, useEffect, useRef, useState } from "react";

import { useApolloClient } from "@apollo/client";

import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_CATEGORY, { UPDATE_CATEGORY } from "../../provider/Categorys/mutation";
import CATEGORYS from "../../provider/Categorys/query";

/** utils */
import { getGraphsqErros, maskRealBeautify } from "../../utilities/functions";
import { useUser } from "../../context/users";

const Crud = () => {
  const settings = useSettings();
  const apollo = useApolloClient();

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;

  const toast = useRef<any>(null);
  const dt = useRef<any>(null);

  const [records, setRecords] = useState<iCategory[]>([]);
  const [record, setRecord] = useState<iCategory>();

  const [loading, setLoading] = useState<boolean>(false);
  const [recordDialog, setRecordDialog] = useState<boolean>(false);
  const [selectedRecords, setSelectedRecords] = useState<any>(null);
  const [deleteRecordDialog, setDeleteRecordDialog] = useState<boolean>(false);
  const [deleteRecordsDialog, setDeleteRecordsDialog] = useState<boolean>(false);

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  // const [rush_hour, setRush_hour] = useState<iRushHour[]>([])

  let emptyRecord: iCategory = {
    name: "",
  };

  if (franchise_id) emptyRecord.franchise_id = franchise_id;

  const loadRecords = (cleanRecord: boolean = false) => {
    if (!franchise_id) return;

    setLoading(true);

    const variables: any = {}

    if (franchise_id) variables.franchise_id = Number(franchise_id);

    apollo
      .query({
        query: CATEGORYS,
        variables,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.Categorys;

        setRecords(data);
        setLoading(false);
      })
      .catch(error => {
        setRecords([]);
        console.log('CATEGORYS', JSON.stringify(error));
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoading(false);
      });
  };

  const openNew = () => {
    setRecord(emptyRecord);
    setSubmitted(false);
    setRecordDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setRecordDialog(false);
  };

  const hideDeleteRecordDialog = () => {
    setDeleteRecordDialog(false);
  };

  const hideDeleteRecordsDialog = () => {
    setDeleteRecordsDialog(false);
  };

  const saveRecord = () => {
    setSubmitted(true);

    if (!record?.name) {
      return;
    }

    const _record: any = { 
      name: record.name,
     };

    setLoading(true);
    let mutation = CREATE_CATEGORY;

    if (record?.id) {
      _record.id = parseInt(`${record.id}`);
      mutation = UPDATE_CATEGORY;
    } else _record.franchise_id = Number(franchise_id);

    apollo
      .mutate({
        mutation,
        variables: {
          ..._record,
        },
      })
      .then(response => {
        setLoading(false);

        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

        loadRecords();
        setRecordDialog(false);
        setRecord(emptyRecord);
      })
      .catch(error => {
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
        console.log('CATEGORY error', JSON.stringify(error));
      });
  };

  const editRecord = (record: iPlan) => {
    const _record: iPlan = { ...record };

    _record.value = maskRealBeautify(parseFloat(`${_record?.value}`) / 100, true);
    setRecord({ ..._record });
    setRecordDialog(true);
  };

  const deleteRecord = () => {
    apollo
      .mutate({
        mutation: UPDATE_CATEGORY,
        variables: {
          data: {
            id: parseInt(`${record?.id}`),
            is_deleted: true,
          },
        },
      })
      .then(response => {
        let _records = records.filter((val: any) => val.id !== record?.id);
        setRecords(_records);
        setDeleteRecordDialog(false);
        setRecord(emptyRecord);
        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
      })
      .catch(error => {
        console.log('Delete Plan error:', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const deleteSelectedRecords = () => {
    let _records = records.filter((val: any) => !selectedRecords.includes(val));
    setRecords(_records);
    setDeleteRecordsDialog(false);
    setSelectedRecords(null);
    toast.current.show({ severity: "success", summary: "Successful", detail: "Records Deleted", life: 3000 });
  };

  const onInputChange = (e: any, name: string) => {
    let val = (e.target && e.target.value) || "";

    if (name === "value") val = maskRealBeautify(val);

    let _record: any = { ...record };
    _record[name] = val;

    setRecord(_record);
  };

  const leftToolbarTemplate = () => {
    return (
      <Fragment>
        <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
      </Fragment>
    );
  };

  const codeBodyTemplate = (rowData: iPlan) => {
    return (
      <>
        <span className="p-column-title">Cód.</span>
        {rowData.id}
      </>
    );
  };

  const nameBodyTemplate = (rowData: iPlan) => {
    return (
      <>
        <span className="p-column-title">Nome</span>
        {rowData.name}
      </>
    );
  };

  const actionBodyTemplate = (rowData: iPlan) => {
    return (
      <div className="actions">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">{loading ? "Carregando categorias..." : "Categorias"}</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
      </span>
    </div>
  );

  const recordDialogFooter = (
    <>
      <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
      <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
    </>
  );

  const deleteRecordDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
    </>
  );

  const deleteRecordsDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={hideDeleteRecordsDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteSelectedRecords} />
    </>
  );

  useEffect(() => {
    loadRecords(true);
  }, [franchise_id]);

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <Toast ref={toast} />

          {franchise_id && !acting_region_id ? loading ? (<i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />) : (
            <>
              <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

              <DataTable
                ref={dt}
                value={records}
                dataKey="id"
                paginator
                stripedRows={true}
                rowHover
                showGridlines
                rows={10}
                rowsPerPageOptions={[5, 10, 25]}
                className="datatable-responsive"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                globalFilter={globalFilter}
                emptyMessage="Nenhum registro encontrado"
                header={header}
                responsiveLayout="scroll">
                <Column field="id" header="Cód." sortable body={codeBodyTemplate} headerStyle={{}}></Column>
                <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}}></Column>

                <Column header="Ações" className="flex flex-row align-items-center justify-content-end" body={actionBodyTemplate}></Column>
              </DataTable>
            </>
          ) : null}

          {/* Modal adicionar editar */}
          <Dialog
            visible={recordDialog}
            style={{ width: "550px" }}
            header={record?.id ? "Detalhes da Categoria" : "Nova Categoria"}
            modal
            className="p-fluid"
            footer={recordDialogFooter}
            onHide={hideDialog}>
            <div className="flex flex-column md:flex-row flex-wrap">

              <div className="field col-12 md:col-6">
                <label htmlFor="name">* Nome</label>
                <InputText
                  id="name"
                  value={record?.name}
                  onChange={e => onInputChange(e, "name")}
                  required
                  autoFocus
                  className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                />
                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
              </div>
            </div>
          </Dialog>

          {/* Modal remover registro unico */}
          <Dialog
            visible={deleteRecordDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordDialogFooter}
            onHide={hideDeleteRecordDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && (
                <span>
                  Tem certeza de que deseja excluir <b>{record?.name}</b>?
                </span>
              )}
            </div>
          </Dialog>

          {/* Modal remover multiplos registros */}
          <Dialog
            visible={deleteRecordsDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordsDialogFooter}
            onHide={hideDeleteRecordsDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && <span>Tem certeza de que deseja excluir os registros selecionados?</span>}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Crud;
